import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useRef, useState } from 'react';
import { ErrorType, recordError } from '../../../../models/apiWrappers/api/exceptions';
import { useMst } from '../../../../models/rootModel';
import { Discount } from '../../../../models/UserPayment';
import { PlanDataExtracted } from '../../../../types/plan';
import { useAnalytics } from '../../../../utils/analytics';
import { calculateAmountWithDiscount, calculatePurchasePrice, formatAmount } from '../../../../utils/price/price.utils';
import { removeUnnecessarySlashesFromUrl } from '../../../../utils/url';
import { StepPropsV2 } from '../../../common/v2/BaseStepV2.types';
import { OnChangeParams } from '../components/PaymentFormV2.types';
import { RoutesNames } from '../../../../routes';
import { getDocumentId } from '../../../../utils/utilsFunctions';
import { sendGTMEvent } from '../../../../utils/googleTagManager';

interface UsePaymentV2Params {
  stepProps: StepPropsV2 & { title?: string };
  currentPlan: PlanDataExtracted;
  discount: Discount | null;
}

export const usePaymentV2 = (props: UsePaymentV2Params) => {
  const { stepProps, currentPlan, discount } = props;
  const { UserDiscoveryResponse, Step, UserPayment } = useMst();

  const stripe = useStripe();
  const elements = useElements();
  const analytics = useAnalytics();

  const [isPaymentElementCompleted, setIsPaymentElementCompleted] = useState<boolean>(false);
  const [paymentErrorAlertText, setPaymentErrorAlertText] = useState('');
  const [isPaymentBeingProcessed, setIsPaymentBeingProcessed] = useState<boolean>(false);
  const [chosenPaymentMethod, setChosenPaymentMethod] = useState<string>('');

  const analyticsObject = useRef({
    ...UserDiscoveryResponse.utms,
    stepId: stepProps.id,
    stepType: stepProps.type,
    stepIndex: stepProps.stepIndex,
    stepTitle: stepProps.title,
    origin: Step.origin,
    userID: UserDiscoveryResponse.accountId,
  });

  const sendGTMPaymentAnalytics = () => {
    const discountAmount = discount?.coupon?.duration
      ? (currentPlan.sanitizedAmount / 100) * (discount.coupon?.percentOff / 100)
      : 0;

    const amount = calculateAmountWithDiscount(currentPlan.sanitizedAmount, discount);

    const currency = 'USD';

    const promoCode = UserPayment.appliedCouponCode || 'NA';
    sendGTMEvent({
      event: 'purchase',
      user_id: UserDiscoveryResponse.accountId,
      phone: UserDiscoveryResponse.userPhoneNumber,
      full_name: UserDiscoveryResponse.userName,
      email: UserDiscoveryResponse.userEmail,
      ecommerce: {
        transaction_id: UserPayment.purchaseId,
        value: calculatePurchasePrice(amount),
        currency, // the currency code, e.g. “USD”
        tax: 0,
        coupon: promoCode, // Coupon code, if applied
        first_time_client: 'yes',
        user_id: UserDiscoveryResponse.accountId,
        items: [
          {
            item_id: UserPayment.planId,
            item_name: 'Premium',
            item_brand: 'Circles',
            coupon: promoCode,
            currency,
            discount: discountAmount.toFixed(2),
            price: formatAmount(currentPlan.sanitizedAmount),
            quantity: 1,
          },
        ],
      },
    });
  };

  const processPayment = async () => {
    if (!stripe || !elements || isPaymentBeingProcessed || !isPaymentElementCompleted) {
      return;
    }

    const redirectionUrl = removeUnnecessarySlashesFromUrl(
      `${process.env.REACT_APP_WEBSITE}${window.location.pathname}/${RoutesNames.PaymentConfirmation}?plan_type=${currentPlan.sanitizedPlanType}&plan_id=${currentPlan.sanitizedId}`
    );

    analytics.track('pay confirmation click', {
      paymentType: chosenPaymentMethod,
      productSku: currentPlan.sanitizedId,
      price: calculateAmountWithDiscount(currentPlan.sanitizedAmount, discount),
      planPrice: currentPlan.sanitizedAmount,
    });

    try {
      const { data, result: canMakePayment } = await UserPayment.canUserMakePayment();

      if (!canMakePayment) {
        throw new Error(data?._uiNotice?.title ?? 'Unexpected error while checking if you can make payment');
      }

      setIsPaymentBeingProcessed(true);

      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw submitError;
      }

      const result = await UserPayment.createPaymentRequest(
        currentPlan.sanitizedId,
        getDocumentId(),
        discount?.coupon?.id,
        UserPayment.klarnaOnly
      );

      if (!result?.success) {
        throw new Error(result.data?._uiNotice?.title ?? 'Unexpected error while creating payment request');
      }

      if ('md' in result.data) {
        const redirectionUrlInMagicDoorMode = `${redirectionUrl}&md=true`;
        window.location.replace(redirectionUrlInMagicDoorMode);
        return;
      }

      const finalize = result.data.mode === 'payment_intent' ? stripe.confirmPayment : stripe.confirmSetup;

      const { error } = await finalize({
        elements,
        clientSecret: result.data.clientSecret,
        confirmParams: {
          return_url: redirectionUrl,
        },
        redirect: 'always',
      });

      if (error) {
        throw error;
      }

      sendGTMPaymentAnalytics();
    } catch (error: any) {
      recordError(ErrorType.HANDLED, error);
      setPaymentErrorAlertText(error?.message ?? 'An error occurred');

      analytics.track(`view payment error`, {
        ...analyticsObject,
        paymentType: chosenPaymentMethod ?? 'unknown',
        productSku: currentPlan.sanitizedId,
        declineReason: error?.code ?? 'unknown',
        error,
      });
    } finally {
      setIsPaymentBeingProcessed(false);
    }
  };

  const onFormChange = (params: OnChangeParams) => {
    setIsPaymentElementCompleted(params.completed);
    setChosenPaymentMethod(params.paymentMethod);
    analytics.track('paywall payment option click', { paymentOption: params.paymentMethod });
  };

  return {
    processPayment,
    isPaymentElementCompleted,
    setPaymentErrorAlertText,
    paymentErrorAlertText,
    isPaymentBeingProcessed,
    setChosenPaymentMethod,
    onFormChange,
  };
};
